<template>
  <div>
    <b-card>
      <b-row>
        <!-- User List -->
        <b-col md="4" class="pr-0">
          <div class="user-list-container">
            <div class="user-list-header">
              <h4>Users</h4>
              <b-form-input v-model="searchQuery" placeholder="Search users..." class="mb-3" />
            </div>
            <div class="user-list">
              <div 
                v-for="user in filteredUsers" 
                :key="user.chatId" 
                class="user-item" 
                :class="{ 'active': selectedUser?.chatId === user.chatId }"
                @click="selectUser(user)"
              >
                <div class="user-avatar">
                  <template v-if="user.avatar">
                    <img :src="user.avatar" alt="User Avatar">
                  </template>
                  <template v-else>
                    <div class="avatar-circle">
                      {{ user.name.charAt(0).toUpperCase() }}
                    </div>
                  </template>
                </div>

                <div class="user-info">
                  <h6>{{ user.name }}</h6>
                    <p class="text-muted">{{ user.lastMessage }}</p>
                </div>
                <div class="user-status" :class="{ 'online': user.online }"></div>
                <div class="unread-count" v-if="user.unread_count && user.unread_count > 0">
                  <span class="badge badge-pill badge-primary">{{ user.unread_count }}</span>
                </div>
            </div>
          </div>
        </div>
        </b-col>

        <!-- Chat Section -->
        <b-col md="8" class="pl-0">
          <div class="chat-container" v-if="selectedUser">
            <div class="chat-header">
              <div class="chat-user-info">
                <img :src="selectedUser.avatar || 'default-avatar.png'" alt="User Avatar">
                <h5>{{ selectedUser.name }}</h5>
              </div>
                <b-button v-if="isChatOpen" variant="danger" size="sm" @click="closeChat" class="ml-auto"> Close Chat </b-button>
                <div v-else style="background: #d4edda; color: #155724; padding: 8px 16px; border-radius: 6px; font-weight: bold;"> Chat Closed </div>
            </div>
            <div class="chat-messages" ref="messagesContainer">
              <div
                v-for="(message, index) in messages"
                :key="index"
                class="message"
                :class="{ 'sent': message.sender === 'me', 'received': message.sender !== 'me' }"
              >
                <div class="message-content">
                   <div v-if="message.image">
                         <a :href="message.image" download target="_blank">
                            <img :src="message.image" alt="Attachment" class="chat-image" />
                         </a>
                    </div>
                      <div v-if="message.file">
                        <a :href="message.file" target="_blank" class="file-link">
                          <i class="fas fa-file-alt"></i> Download Attachment
                        </a>
                      </div>
                    <p v-if="message.text">{{ message.text }}</p>
                </div>
              </div>
            </div>
            <div class="chat-input" v-if="isChatOpen">
              <b-form @submit.prevent="sendMessage">
                <b-input-group>
                    <template #prepend>
                      <b-button variant="outline-secondary" @click="$refs.fileInput.click()" class="mr-2" title="Attach Image">
                        <i class="fas fa-paperclip"></i>
                      </b-button>
                      <input
                        ref="fileInput"
                        type="file"
                        accept="image/*"
                        style="display: none"
                        @change="handleFileUpload"
                      />
                    </template>
                    <!-- Show selected file/image preview with fixed area -->
                    <div v-if="file" class="ml-2 d-flex align-items-center">
                      <div v-if="selectedFilePreview" style="width: 60px; height: 60px; border: 1px solid #ddd; border-radius: 6px; overflow: hidden; display: flex; align-items: center; justify-content: center; background: #fafafa;">
                        <img
                          :src="selectedFilePreview"
                          alt="Selected"
                          style="max-width: 100%; max-height: 100%; object-fit: cover;"
                        />
                      </div>
                      <span v-else style="max-width: 120px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">{{ file.name }}</span>
                      <b-button size="sm" variant="link" @click="removeFile" title="Remove file">
                        <i class="fas fa-times"></i>
                      </b-button>
                    </div>

                  <b-form-input
                    v-model="newMessage"
                    placeholder="Type a message..."
                  />
                  <b-input-group-append>
                    <b-button type="submit" variant="primary">Send</b-button>
                  </b-input-group-append>
                </b-input-group>
              </b-form>
            </div>
          </div>
          <div class="no-chat-selected" v-else>
            <div class="empty-state">
              <i class="fas fa-comments"></i>
              <h4>Select a user to start chatting</h4>
              <p>Choose from the list</p>
            </div>
          </div>
        </b-col>
      </b-row>
    </b-card>
  </div>
</template>

<script>
import {
  BRow, BCol, BCard, BButton, BForm,  BFormInput, BInputGroup, BInputGroupAppend, BFormFile
} from "bootstrap-vue";
import axios from "../../../axios";
import Pusher from 'pusher-js';

export default {
  data() {
    return {
      users: [],
      selectedUser: null,
      messages: [],
      newMessage: '',
      searchQuery: '',
      currentAgentId: null,
      pusher: null,
      file: null
    };
  },
  components: {
    BRow, BCol, BCard, BButton, BForm, BFormInput, BInputGroup, BInputGroupAppend, BFormFile
  },
  computed: {
    filteredUsers() {
      return Array.isArray(this.users)
        ? this.users.filter(user => user.name.toLowerCase().includes(this.searchQuery.toLowerCase()))
        : [];
    },
    isChatOpen() {
      // backend writes 'Open'/'Closed', older rows use lowercase
      return !!this.selectedUser && String(this.selectedUser.status).toLowerCase() === 'open';
    },
    selectedFilePreview() {
      return this.file && this.file.type && this.file.type.startsWith('image/')
        ? URL.createObjectURL(this.file)
        : null;
    }
  },
created() {
  this.currentAgentId = Number(localStorage.getItem('userId')) || null;
  this.fetchUsers();
  this.initPusher();
},
methods: {
    handleFileUpload(event) {
    this.file = event.target.files[0];
    event.target.value = '';
  },
   initPusher() {
      this.pusher = new Pusher('b89af738d362d3ca27d2', {
        cluster: 'ap2',
        encrypted: true,
      });

      const channel = this.pusher.subscribe('chat-channel');
          channel.bind('chat-event', data => {
            // Find the user in the list by chat_id (not receiver_id)
            const userIndex = this.users.findIndex(u => u.chatId === data.chat_id);
            // If this chat is active, show message in chat window
            if (data.chat_id === this.selectedUser?.chatId) {
              // Only push if not sent by me (avoid duplicates)
              if (data.sender_id !== this.currentAgentId) {
            const message = { sender: 'them', text: data.message || '' };
            if (data.attachments) {
              const fullPath = `https://wossdb.wossautoid.com/${data.attachments}`;
              const isImage = /\.(jpe?g|png|gif|bmp|webp)$/i.test(data.attachments);
              if (isImage) {
                message.image = fullPath;
              } else {
                message.file = fullPath;
              }
            } else {
              message.text = data.message;
            }
            this.messages.push(message);
            this.$nextTick(this.scrollToBottom);
              }
            }
            // Update user list for last message and sort
            if (userIndex !== -1) {
              this.users[userIndex].lastMessage = data.message || 'Attachment';
              this.users[userIndex].lastMessageTime = new Date();
              
              if (!this.selectedUser || this.selectedUser.chatId !== data.chat_id) {
                this.users[userIndex].unread_count = (this.users[userIndex].unread_count || 0) + 1;
              }
              this.sortUsersByLastMessageTime();
            } else {
              // If new chat, fetch users again to update the list
              this.fetchUsers();
            }
          });
      },

    fetchUsers() {
      axios.get('/userlist').catch(error => {
        console.error('userlist failed:', error.response && error.response.status, error.response && error.response.data);
        this.$bvToast.toast(
          (error.response && error.response.data && error.response.data.message) || 'Could not load the user list.',
          { title: 'Chat', variant: 'danger', solid: true }
        );
        return null;
      }).then(res => {
        if (res && res.data.success && Array.isArray(res.data.data)) {
          this.users = res.data.data.map(chat => {
            const lastMsgObj = Array.isArray(chat.messages) && chat.messages.length > 0
              ? chat.messages[chat.messages.length - 1]
              : null;
            return {
              id: chat.user.id,
              name: `${chat.user.first_name} ${chat.user.second_name || ''} ${chat.user.last_name || ''}`.trim(),
              avatar: chat.user.image ? `https://wossdb.wossautoid.com/${chat.user.image}` : null,
              lastMessage: lastMsgObj ? lastMsgObj.message : '',
              lastMessageTime: lastMsgObj ? lastMsgObj.created_at : null,
              chatId: chat.id,
              online: chat.user.online || true,
              unread_count: chat.unread_count || 0
            };
          });
          this.sortUsersByLastMessageTime();
        }
      });
    },

    selectUser(user) {
    axios.get(`/getChatById/${user.chatId}`).then(res => {
      if (res.data.success) {
        const chat = res.data.data;

        // Properly map messages with file/image distinction
        this.messages = chat.messages.map(m => {
          const message = {
            sender: m.sender_id === this.currentAgentId ? 'me' : 'them',
            text: m.message,
          };

          if (m.attachments) {
            const fullPath = `https://wossdb.wossautoid.com/${m.attachments}`;
            const isImage = /\.(jpe?g|png|gif|bmp|webp)$/i.test(m.attachments);

            if (isImage) {
              message.image = fullPath;
            } else {
              message.file = fullPath;
            }
          }

          return message;
        });

        // Set the selected user/chat info
        this.selectedUser = {
          ...chat,
          name: `${chat.user.first_name} ${chat.user.second_name || ''} ${chat.user.last_name || ''}`.trim(),
          avatar: chat.user.image ? `https://wossdb.wossautoid.com/${chat.user.image}` : null,
          chatId: chat.id,
          id: chat.user.id
        };

         const index = this.users.findIndex(u => u.chatId === chat.id);
            if (index !== -1 && this.users[index].unread_count > 0) {
        this.users[index].unread_count = 0;
      }
        this.$nextTick(this.scrollToBottom);
      }
    }).catch(error => {
      console.error('getChatById failed:', error.response && error.response.status, error.response && error.response.data);
      this.$bvToast.toast(
        (error.response && error.response.data && error.response.data.message) || 'Could not load this conversation.',
        { title: 'Chat', variant: 'danger', solid: true }
      );
    });
  },

    sendMessage() {
    if (!this.newMessage.trim() && !this.file) return;

    const formData = new FormData();
    formData.append("chat_id", this.selectedUser.chatId);
    formData.append("sender_id", this.currentAgentId);
    formData.append("receiver_id", this.selectedUser.id);
    formData.append("message", this.newMessage);
    if (this.file) {
      formData.append("attachments", this.file);
    }

    axios.post('sendmessagetoUser', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    }).then(res => {
      if (res.data.success) {
        const msgObj = { sender: 'me' };
        if (this.newMessage) msgObj.text = this.newMessage;
        if (this.file && res.data.data && res.data.data.attachments) {
          const fullPath = `https://wossdb.wossautoid.com/${res.data.data.attachments}`;
          const isImage = /\.(jpe?g|png|gif|bmp|webp)$/i.test(res.data.data.attachments);
          if (isImage) {
            msgObj.image = fullPath;
          } else {
            msgObj.file = fullPath;
          }
        }
        this.messages.push(msgObj);
        const index = this.users.findIndex(u => u.id === this.selectedUser.id);
        if (index !== -1) this.users[index].lastMessage = this.newMessage || 'Image';
        this.users[index].lastMessageTime = new Date(); // Add timestamp
        this.sortUsersByLastMessageTime();
        this.newMessage = '';
        this.file = null;
        this.$nextTick(this.scrollToBottom);
      }
    });
  },
    scrollToBottom() {
      this.$nextTick(() => {
        const container = this.$refs.messagesContainer;
        if (container) {
          container.scrollTop = container.scrollHeight;
        }
      });
    },
    sortUsersByLastMessageTime() {
      this.users.sort((a, b) => {
        return new Date(b.lastMessageTime || 0) - new Date(a.lastMessageTime || 0);
      });
    },
    closeChat() {
      if (!this.selectedUser) return;
      axios.post(`/closeChat/${this.selectedUser.chatId}`).then(res => {
        if (res.data.success) {
          this.selectedUser = null;
          this.messages = [];
          this.fetchUsers();
        }
      });
    },
      removeFile() {
      this.file = null;
      if (this.$refs.fileInput) this.$refs.fileInput.value = '';
    }
  }
};
</script>

<style scoped>
.user-list-container {
  height: 600px;
  border-right: 1px solid #eee;
  display: flex;
  flex-direction: column;
}

.user-list-header {
  padding: 15px;
  border-bottom: 1px solid #eee;
}

.user-list {
  flex: 1;
  overflow-y: auto;
}

.user-item {
  display: flex;
  align-items: center;
  padding: 12px 15px;
  cursor: pointer;
  border-bottom: 1px solid #f5f5f5;
  transition: background-color 0.2s;
}

.user-item:hover {
  background-color: #f9f9f9;
}

.user-item.active {
  background-color: #e6f2ff;
}

.user-avatar {
  margin-right: 12px;
}

.user-avatar img {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
}

.user-info {
  flex: 1;
}

.user-info h6 {
  margin-bottom: 2px;
  font-size: 14px;
}

.user-info p {
  margin: 0;
  font-size: 12px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.user-status {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: #ccc;
}

.user-status.online {
  background-color: #28a745;
}

.chat-container {
  height: 600px;
  display: flex;
  flex-direction: column;
}

.chat-header {
  padding: 15px;
  border-bottom: 1px solid #eee;
  display: flex;
  align-items: center;
}

.chat-user-info {
  display: flex;
  align-items: center;
}

.chat-user-info img {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  margin-right: 10px;
}

.chat-user-info h5 {
  margin: 0;
  margin-right: 10px;
}

.status-indicator {
  font-size: 12px;
  color: #6c757d;
}

.status-indicator.online {
  color: #28a745;
}

.chat-messages {
  flex: 1;
  padding: 15px;
  overflow-y: auto;
  background-color: #f5f5f5;
}

.message {
  margin-bottom: 15px;
  display: flex;
}

.message.sent {
  justify-content: flex-end;
}

.message.received {
  justify-content: flex-start;
}

.message-content {
  max-width: 70%;
  padding: 10px 15px;
  border-radius: 18px;
  position: relative;
}

.message.sent .message-content {
  background-color: #007bff;
  color: white;
}

.message.received .message-content {
  background-color: white;
  border: 1px solid #eee;
}

.message-time {
  display: block;
  font-size: 10px;
  margin-top: 5px;
  opacity: 0.8;
}

.message.sent .message-time {
  text-align: right;
  color: rgba(255, 255, 255, 0.7);
}

.message.received .message-time {
  text-align: left;
  color: #6c757d;
}

.chat-input {
  padding: 15px;
  border-top: 1px solid #eee;
  background-color: white;
}
.chat-image {
  max-width: 200px;
  border-radius: 8px;
  margin-top: 5px;
}

.no-chat-selected {
  height: 600px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f5f5f5;
}

.empty-state {
  text-align: center;
  color: #6c757d;
}

.empty-state i {
  font-size: 48px;
  margin-bottom: 15px;
  color: #adb5bd;
}

.empty-state h4 {
  margin-bottom: 10px;
}

.empty-state p {
  margin: 0;
}

.file-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #ffffff;
  text-decoration: none;
}

.message.sent .file-link {
  color: #fff;
}

.message.received .file-link {
  color: #000;
}

.file-link i {
  font-size: 16px;
}
.avatar-circle {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: #6c757d;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 18px;
}

</style>
  